import { useMemo, useState } from 'react';

import { useSkills } from '../../hooks/use-skills';
import { ResizableListPane } from '../../shell/ResizableListPane';

import { SkillDetail } from './SkillDetail';
import { SkillList } from './SkillList';

/**
 * Top-level admin Skills view. Two content panes (list + detail) inside the
 * existing workspace shell — backend gates with `creator_mode | full_access`,
 * and the nav-item gate matches.
 */
export function SkillsView() {
  const {
    skills,
    status,
    error,
    selectedId,
    setSelectedId,
    detail,
    detailStatus,
    detailError,
  } = useSkills();
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return skills;
    return skills.filter(
      (s) => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q),
    );
  }, [skills, filter]);

  // The list endpoint (GET /skills) is currently open to any authed user (it's
  // also consumed by the composer's skill picker). The per-skill admin endpoints
  // ARE gated. If we get a 403 from the detail call, surface a clear "access
  // denied" message — covers the case where a tab persists across role changes.
  const accessDenied =
    typeof detailError === 'string' && detailError.toLowerCase().includes('creator_mode');

  // Auto-select the first skill once the list loads (if nothing is selected).
  if (!selectedId && filtered.length > 0 && !accessDenied) {
    const first = filtered[0];
    if (first) {
      // Defer to avoid an in-render state update.
      queueMicrotask(() => setSelectedId(first.id));
    }
  }

  if (accessDenied) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'var(--bg)',
        }}
      >
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
            Skills admin is restricted
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)', lineHeight: 1.5 }}>
            You don't have the <code>creator_mode</code> capability required to view the Skills
            admin. Ask a workspace admin to grant it, then reload.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0, background: 'var(--bg)' }}>
      {/* List pane — resizable; width persists per-view. */}
      <ResizableListPane
        storageKey="skills"
        defaultWidth={320}
        minWidth={240}
        maxWidth={560}
        paneStyle={{
          borderRight: '1px solid var(--border)',
          overflowY: 'auto',
          background: 'var(--bg-alt)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            background: 'var(--bg-alt)',
            zIndex: 1,
          }}
        >
          <input
            placeholder="Filter skills…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter skills"
            style={{
              flex: 1,
              minWidth: 0,
              height: 30,
              boxSizing: 'border-box',
              padding: '0 10px',
              fontSize: 12,
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              background: 'var(--surface)',
              outline: 'none',
              color: 'var(--text)',
              fontFamily: 'inherit',
            }}
          />
        </div>
        {status === 'loading' && skills.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>Loading skills…</div>
        )}
        {status === 'error' && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--kind-failure)' }}>
            {error ?? 'Failed to load skills.'}
          </div>
        )}
        {status === 'success' && filtered.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>
            {filter ? 'No skills match that filter.' : 'No skills available.'}
          </div>
        )}
        <SkillList skills={filtered} selectedId={selectedId} onSelect={setSelectedId} />
      </ResizableListPane>

      {/* Detail pane */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {selectedId ? (
          <SkillDetail
            skillId={selectedId}
            detail={detail}
            detailStatus={detailStatus}
            detailError={detailError}
          />
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-mute)',
              fontSize: 13,
            }}
          >
            Select a skill from the left to view its details.
          </div>
        )}
      </div>
    </div>
  );
}
